import React, { useState, useCallback, useRef } from "react"
import { Button, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from "@material-ui/core"
import { Add as AddIcon, Close as CloseIcon } from "@material-ui/icons"
import { ActionDialog, Notification } from "@/components"
import useCustomStyles from "@/styles/custom"
import ContactRegisterDateFilterConditions from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactRegisterDateFilterCondition"
import { renderComponent, unmountComponent } from "@/utils/node"
import ContactRegisteredFromFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactRegisteredFromFilterCondition"
import ContactCreatedByImportFeatureFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactCreatedByImportFeatureFilterCondition"
import ContactUpdateDateFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactUpdateDateFilterCondition"
import ContactUpdatedByImportFeatureFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactUpdatedByImportFeatureFilterCondition"
import ContactHealthFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactHealthFilterCondition"
import ContactBoardFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactBoardFilterCondition"
import ContactTrackableLinksFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactTrackableLinksFilterCondition"
import ContactAccountManagerFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactAccountManagerFilterCondition"
import WABACommunicationPermissionFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/WABACommunicationPermissionFilterCondition"
import MetaFreeEntryPointWindowFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/MetaFreeEntryPointWindowFilterCondition"
import { ConditionOperator, ContactFilters, ContactsFiltersConditions, BaseContactsFiltersType, FilterType, FilterTypeToFilterData } from "@/protocols/ClientCatalogFilterProtocol"
import ContactLastTransactedMessageDateFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactLastTransactedMessageDateFilterCondition"
import ContactTagFilterCondition from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter/Conditions/ContactTagFilterCondition"
import { ContactFilterModeData } from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters"

const possibleFilterOptionsLabels: Record<FilterType, string> = {
	contact_register_date: "Data de Criação",
	contact_update_date: "Data de atualização",
	contact_tag: "Tag",
	contact_last_message_date: "Data de última mensagem transacionada",
	contact_registered_from: "Origem de cadastro do contato",
	client_catalog_contacts_created_by_contacts_import: "Criado por importação de contato específica",
	client_catalog_contacts_updated_by_contacts_import: "Atualizado por importação de contato específica",
	contact_health: "Contatos que",
	contact_board: "Funil",
	contact_trackable_links: "Links rastreáveis",
	contact_account_manager: "Atendente responsável",
	contact_waba_communication_permission: "Permissão de comunicação WABA",
	contact_meta_free_entry_point_window: "Modo Meta: custo reduzido"
}

type PossibleFilterOption = {
	type: FilterType
	label: string
}

const defaultFilterData: Record<FilterType, FilterTypeToFilterData[FilterType]> = {
	contact_register_date: { dateFrom: new Date(), dateTo: new Date() },
	contact_update_date: { dateFrom: new Date(), dateTo: new Date() },
	contact_last_message_date: { dateFrom: new Date(), dateTo: new Date() },
	contact_tag: { tag_id: "" },
	contact_registered_from: { registered_from: "message_blast" },
	client_catalog_contacts_created_by_contacts_import: { client_catalog_contacts_created_by_contacts_import_id: 0 },
	client_catalog_contacts_updated_by_contacts_import: { client_catalog_contacts_updated_by_contacts_import_id: 0 },
	contact_health: { status: "no-exists" },
	contact_board: {
		contact_board_id: 0,
		contact_board_stage_id: 0
	},
	contact_trackable_links: {
		contact_trackable_links_id: 0
	},
	contact_account_manager: {
		account_manager_user_id: 0
	},
	contact_waba_communication_permission: { status: "active" },
	contact_meta_free_entry_point_window: { status: "active" }
}

const defaultOperators: Partial<Record<FilterType, ConditionOperator>> = {
	contact_health: "equal",
	contact_tag: "in",
	contact_registered_from: "equal",
	contact_account_manager: "equal",
	contact_board: "in",
	contact_waba_communication_permission: "equal",
	contact_meta_free_entry_point_window: "equal"
}

const filterConditionsTypes: PossibleFilterOption[] = (
	Object.keys(possibleFilterOptionsLabels) as FilterType[]
).map(type => ({ type, label: possibleFilterOptionsLabels[type] }))

type ManageContactFilterProps = {
	filter?: BaseContactsFiltersType
	dialogOptions: ContactFilterModeData
	handleUseFilters: (filter?: BaseContactsFiltersType) => void
	handleUpdateFilterData: (updatedFilter: BaseContactsFiltersType) => void
}

const ManageContactFilter: React.FC<ManageContactFilterProps> & {
	open: (props: ManageContactFilterProps) => void
	close: () => void
} = (props) => {
	const { filter, dialogOptions } = props

	const [currentFilter, setCurrentFilter] = useState<BaseContactsFiltersType>(filter || {
		mainOperator: "and",
		title: "",
		conditions: [
			{
				type: "contact_register_date",
				operator: "between",
				filterData: defaultFilterData.contact_register_date
			}
		]
	})
	const [openDialog, setOpenDialog] = useState(true)
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const customClasses = useCustomStyles()

	const handleUpdateContactFilters = (key: keyof BaseContactsFiltersType, value: string | BaseContactsFiltersType["conditions"]) => {
		setCurrentFilter(prev => ({
			...prev,
			[key]: value
		}))
	}

	const addNewCondition = useCallback(() => {
		const defaultNewCondition: ContactsFiltersConditions<FilterType> = {
			type: "contact_register_date",
			operator: "between",
			filterData: defaultFilterData.contact_register_date
		}

		handleUpdateContactFilters("conditions", [
			...(currentFilter?.conditions || []),
			defaultNewCondition
		])

		setTimeout(() => {
			scrollContainerRef.current?.scrollTo({
				top: scrollContainerRef.current.scrollHeight,
				behavior: "smooth"
			})
		}, 100)
	}, [JSON.stringify(currentFilter?.conditions)])

	const handleRemoveFilterCondition = useCallback((index: number) => {
		const conditions = currentFilter.conditions?.filter((_, filterIndex) => filterIndex !== index) || []
		handleUpdateContactFilters("conditions", conditions)
	}, [JSON.stringify(currentFilter?.conditions)])

	const handleConditionTypeChange = useCallback((type: FilterType, index: number) => {
		const operator = defaultOperators[type] ?? "between"

		const conditions = currentFilter.conditions?.map((condition, idx) => {
			if (idx === index) {
				return {
					type,
					operator,
					filterData: defaultFilterData[type]
				} as ContactsFiltersConditions<FilterType>
			}
			return condition
		}) || []

		handleUpdateContactFilters("conditions", conditions)
	}, [JSON.stringify(currentFilter?.conditions)])

	const handleChangeFilterCondition = useCallback((
		newConditions: ContactsFiltersConditions<FilterType>,
		index: number
	) => {
		const conditions = currentFilter.conditions?.map((condition, idx) => {
			if (idx === index) {
				return {
					...condition,
					...newConditions
				} as ContactsFiltersConditions<FilterType>
			}
			return condition
		}) || []

		handleUpdateContactFilters("conditions", conditions)
	}, [JSON.stringify(currentFilter?.conditions)])

	const handleFilterChange = (key: keyof ContactFilters, value: string) => {
		const filterTypeToFilterTypeHandler: Record<keyof ContactFilters, () => void> = {
			mainOperator: () => handleUpdateContactFilters(key, value),
			title: () => handleUpdateContactFilters("title", value)
		}

		const filterHandler = filterTypeToFilterTypeHandler[key]

		if (filterHandler) {
			filterHandler()
		}
	}

	const getFilterOptionsByFilterConditions = useCallback((condition: ContactsFiltersConditions<keyof FilterTypeToFilterData>, index: number) => {
		const type = condition.type

		const filterTypeToFilterOptions: Record<FilterType, JSX.Element> = {
			contact_register_date: <ContactRegisterDateFilterConditions
				condition={condition as ContactsFiltersConditions<"contact_register_date">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_update_date: <ContactUpdateDateFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_update_date">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_last_message_date: <ContactLastTransactedMessageDateFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_last_message_date">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_tag: <ContactTagFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_tag">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_registered_from: <ContactRegisteredFromFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_registered_from">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			client_catalog_contacts_created_by_contacts_import: <ContactCreatedByImportFeatureFilterCondition
				condition={condition as ContactsFiltersConditions<"client_catalog_contacts_created_by_contacts_import">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			client_catalog_contacts_updated_by_contacts_import: <ContactUpdatedByImportFeatureFilterCondition
				condition={condition as ContactsFiltersConditions<"client_catalog_contacts_updated_by_contacts_import">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_health: <ContactHealthFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_health">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_board: <ContactBoardFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_board">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_trackable_links: <ContactTrackableLinksFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_trackable_links">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_account_manager: <ContactAccountManagerFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_account_manager">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_waba_communication_permission: <WABACommunicationPermissionFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_waba_communication_permission">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />,
			contact_meta_free_entry_point_window: <MetaFreeEntryPointWindowFilterCondition
				condition={condition as ContactsFiltersConditions<"contact_meta_free_entry_point_window">}
				onChange={(updatedCondition) => handleChangeFilterCondition(updatedCondition, index)} />
		}

		return filterTypeToFilterOptions[type]
	}, [handleChangeFilterCondition])

	const handleCloseDialog = () => {
		setOpenDialog(false)
		dialogOptions.onClose?.()
		ManageContactFilter.close()
	}

	const isContactBoardValueRangeInverted = (condition: ContactsFiltersConditions<FilterType>): boolean => {
		if (condition.type !== "contact_board" || condition.operator !== "between") {
			return false
		}

		const { contact_value_from_in_cents, contact_value_to_in_cents } = condition.filterData as FilterTypeToFilterData["contact_board"]

		const hasBothValues = contact_value_from_in_cents !== undefined && contact_value_to_in_cents !== undefined

		return hasBothValues && contact_value_from_in_cents > contact_value_to_in_cents
	}

	const validateFilterBeforeSave = (): boolean => {
		const hasInvertedValueRange = currentFilter.conditions?.some(isContactBoardValueRangeInverted)

		if (hasInvertedValueRange) {
			Notification.error({ message: "No filtro de Valor do contato, o valor mínimo não pode ser maior que o máximo." })
		}

		return !hasInvertedValueRange
	}

	const handleSave = async () => {
		if (!validateFilterBeforeSave()) {
			return
		}

		if (dialogOptions.onSave) {
			await dialogOptions.onSave(currentFilter)
			handleCloseDialog()
		}
	}

	const handleSecondaryAction = async () => {
		if (!validateFilterBeforeSave()) {
			return
		}

		if (dialogOptions.onSecondaryAction) {
			await dialogOptions.onSecondaryAction(currentFilter)
			handleCloseDialog()
		}
	}

	return (
		<ActionDialog
			title={dialogOptions?.title || ""}
			openDialog={openDialog}
			onClose={handleCloseDialog}
			onSave={handleSave}
			{...(dialogOptions?.onSecondaryAction && {
				onSecondaryAction: handleSecondaryAction,
				secondaryActionText: dialogOptions.secondaryActionText
			})}
			saveText={dialogOptions?.saveText}
			maxWidth="xl"
		>
			<Grid container direction="column" spacing={3}>
				{!dialogOptions?.disableNameInput && (
					<Grid item xs={12}>
						<Grid container>
							<Grid item xs={12}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<InputLabel className={customClasses.inputLabel}>
											Nome do filtro
										</InputLabel>
									</Grid>
									<Grid item xs={12}>
										<TextField
											disabled={dialogOptions?.disableNameInput}
											name="name"
											value={currentFilter?.title || ""}
											onChange={({ target }) => handleFilterChange("title", target.value)}
											variant="outlined"
											placeholder="Nome do filtro"
											fullWidth
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				)}

				<Grid item xs={12}>
					<Grid container>
						<Grid item xs={12}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Grid container direction="column">
										<Grid item>
											<InputLabel className={customClasses.inputLabel}>
												Os contatos devem atender a:
											</InputLabel>
										</Grid>
										<Grid item style={{ lineHeight: "14px" }}>
											<Typography variant="caption">Define se os contatos precisam atender a todas as condições configuradas ou apenas a pelo menos uma delas para ser incluído no resultado.</Typography>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<Select
										variant="outlined"
										value={currentFilter.mainOperator}
										onChange={({ target }) => handleFilterChange("mainOperator", target.value as string)}
										style={{ width: "100%" }}
									>
										<MenuItem value="and">Todas as condições</MenuItem>
										<MenuItem value="or">Pelo menos uma das condições</MenuItem>
									</Select>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<Grid container>
						<Grid item xs={12}>
							<Grid container spacing={2}>
								<Grid item>
									<InputLabel className={customClasses.inputLabel}>
										Condições
									</InputLabel>
								</Grid>
								<Grid item xs={12} ref={scrollContainerRef} style={{ maxHeight: 280, overflowY: "auto", overflowX: "hidden" }}>
									<Grid
										container
										direction="column"
										spacing={2}>
										{currentFilter.conditions?.map((condition, index) => (
											<Grid item key={condition.type + index}>
												<Grid container alignItems="center" spacing={1}>
													<Grid item xs={3}>
														<FormControl variant="outlined" fullWidth size="small">
															<Select
																value={condition.type}
																onChange={e => handleConditionTypeChange(e.target.value as FilterType, index)}
															>
																{filterConditionsTypes.map(option => (
																	<MenuItem key={option.type} value={option.type}>
																		<b>{option.label}</b>
																	</MenuItem>
																))}
															</Select>
														</FormControl>
													</Grid>
													<Grid item xs>
														<FormControl variant="outlined" fullWidth size="small">
															{getFilterOptionsByFilterConditions(condition, index)}
														</FormControl>
													</Grid>
													<Grid item>
														<IconButton size="small" onClick={() => handleRemoveFilterCondition(index)}>
															<CloseIcon fontSize="small" />
														</IconButton>
													</Grid>
												</Grid>
											</Grid>
										))}
									</Grid>
								</Grid>
								<Grid item>
									<Button
										variant="outlined"
										color="primary"
										onClick={addNewCondition}
										style={{ height: 40, fontSize: 14 }}
										startIcon={<AddIcon />}
									>
										Adicionar condição
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</ActionDialog>
	)
}

const manageFilterContainerId = "manage-contact-filter-root"

ManageContactFilter.open = (props: ManageContactFilterProps) => {
	renderComponent(
		manageFilterContainerId,
		<ManageContactFilter {...props} />
	)
}

ManageContactFilter.close = () => {
	unmountComponent(manageFilterContainerId)
}

export default ManageContactFilter
